import { phoneQueryDigits } from '@orn/global/admin/accountSearch.js';
import { isShortUuid, isValidUuid } from '@orn/global/ids/uuid.js';

import { PATHS, accountsFamilyPath, objectInspectorPath } from '../../paths';
import AccountsIcon from '../../scenes/Accounts/AccountsIcon';
import ObjectInspectorIcon from '../../scenes/ObjectInspector/ObjectInspectorIcon';

import type { ITool } from '../../tools';

/** User rows the palette lists at most; the section header says how many matched. */
export const MAX_USER_MATCHES = 8;

/** One row of the palette: how it reads, and where Enter takes the operator. */
export interface IPaletteItem {
  /** Unique across every section — the React key. */
  key: string;
  title: string;
  subtitle: string;
  /** Right-aligned and monospaced: the route the row opens. */
  hint: string;
  href: string;
  icon: ITool['icon'];
  /** Ids, emails, and phone numbers read in monospace. */
  isTitleMono: boolean;
}

/** A labeled run of rows. Keyboard selection spans every section as one list. */
export interface IPaletteSection {
  label: string;
  items: IPaletteItem[];
  /** Rows that matched before the cap — above `items.length` when the section was cut. */
  matchCount: number;
}

/** The fields of an `adminListUsers` row the palette reads. */
export interface IPaletteUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  family: { id: string };
}

/**
 * True when the query holds an `@` or reads as a phone number (see
 * `phoneQueryDigits`), neither of which any tool name or keyword does — so the
 * user list loads only for a lookup that could name a person, and typing a
 * tool's name never lists people.
 */
export function isUserLookupQuery(query: string): boolean {
  return isEmailQuery(query) || phoneQueryDigits(query) !== null;
}

function isEmailQuery(query: string): boolean {
  return query.includes('@');
}

/**
 * Every section a query produces, in the order Enter should prefer them. A
 * pasted id leads because it can mean only one thing; tools come before users
 * because the palette is a tool switcher first.
 */
export function buildPaletteSections(
  query: string,
  tools: readonly ITool[],
  users: readonly IPaletteUser[]
): IPaletteSection[] {
  const sections: IPaletteSection[] = [];

  const inspectItem = inspectIdItem(query);
  if (inspectItem !== null) {
    sections.push({ label: 'Inspect', items: [inspectItem], matchCount: 1 });
  }

  const toolItems = filterTools(tools, query).map(toolItem);
  if (toolItems.length > 0) {
    sections.push({ label: 'Tools', items: toolItems, matchCount: toolItems.length });
  }

  const userItems = matchUserItems(users, query);
  if (userItems.length > 0) {
    sections.push({
      label: 'Users',
      items: userItems.slice(0, MAX_USER_MATCHES),
      matchCount: userItems.length,
    });
  }

  return sections;
}

/**
 * Tools matching the query: every whitespace-separated token must appear
 * (case-insensitive, substring) in the tool's name + keywords. Good enough for
 * a registry of ~20 tools, with no dependency. An empty query matches all.
 */
export function filterTools(tools: readonly ITool[], query: string): ITool[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return [...tools];
  }
  const tokens = q.split(/\s+/);
  return tools.filter((tool) => {
    const haystack = [tool.name, ...tool.keywords].join(' ').toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}

/**
 * The row that opens a whole uuid, or its eight-character short form, in the
 * Object inspector; null for anything else. Recognized by shape alone — the
 * inspector reports an id that names no row, so nothing is searched here.
 */
function inspectIdItem(query: string): IPaletteItem | null {
  const id = query.trim();
  if (!isValidUuid(id) && !isShortUuid(id)) {
    return null;
  }
  return {
    key: `inspect:${id}`,
    title: id,
    subtitle: 'Look this id up across every inspectable table',
    hint: PATHS.OBJECT_INSPECTOR,
    href: objectInspectorPath(id),
    icon: ObjectInspectorIcon,
    isTitleMono: true,
  };
}

/** A user row per match, by email for a query holding an `@` and by phone number for one reading as a number. */
function matchUserItems(users: readonly IPaletteUser[], query: string): IPaletteItem[] {
  if (isEmailQuery(query)) {
    return matchUsersByEmail(users, query).map((user) =>
      userItem(user, user.email, fullName(user) || 'No name on file')
    );
  }
  const digits = phoneQueryDigits(query);
  if (digits === null) {
    return [];
  }
  return matchUsersByPhone(users, digits).map(({ user, phoneNumber }) =>
    userItem(user, phoneNumber, [fullName(user), user.email].filter(Boolean).join(' · '))
  );
}

/**
 * Users whose email contains the query, best match first: the exact address,
 * then addresses starting with it, then the rest, each run alphabetical. The
 * exact address leads so a pasted email opens its own household on Enter even
 * when it also sits inside another (`a@x.com` in `da@x.com`).
 */
function matchUsersByEmail(users: readonly IPaletteUser[], query: string): IPaletteUser[] {
  const needle = query.trim().toLowerCase();
  return users
    .map((user) => ({ user, email: user.email.toLowerCase() }))
    .filter(({ email }) => email.includes(needle))
    .sort((a, b) => emailRank(a.email, needle) - emailRank(b.email, needle) || a.email.localeCompare(b.email))
    .map(({ user }) => user);
}

function emailRank(email: string, needle: string): number {
  if (email === needle) {
    return 0;
  }
  return email.startsWith(needle) ? 1 : 2;
}

/**
 * Users whose number holds the query's digits, numbers ending in them first —
 * the whole number typed without its country code, or its last four — then the
 * rest, each run in number order. Digits on both sides, so neither the query's
 * punctuation nor a number stored before E.164 canonicalization hides a match.
 */
function matchUsersByPhone(
  users: readonly IPaletteUser[],
  digits: string
): Array<{ user: IPaletteUser; phoneNumber: string }> {
  return users
    .flatMap((user) =>
      user.phoneNumber
        ? [{ user, phoneNumber: user.phoneNumber, phoneDigits: user.phoneNumber.replace(/\D/g, '') }]
        : []
    )
    .filter(({ phoneDigits }) => phoneDigits.includes(digits))
    .sort(
      (a, b) =>
        phoneRank(a.phoneDigits, digits) - phoneRank(b.phoneDigits, digits) ||
        a.phoneDigits.localeCompare(b.phoneDigits)
    );
}

function phoneRank(phoneDigits: string, digits: string): number {
  return phoneDigits.endsWith(digits) ? 0 : 1;
}

function toolItem(tool: ITool): IPaletteItem {
  return {
    key: `tool:${tool.id}`,
    title: tool.name,
    subtitle: tool.description,
    hint: tool.path,
    href: tool.path,
    icon: tool.icon,
    isTitleMono: false,
  };
}

function fullName(user: IPaletteUser): string {
  return [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ');
}

function userItem(user: IPaletteUser, title: string, subtitle: string): IPaletteItem {
  return {
    key: `user:${user.id}`,
    title,
    subtitle,
    hint: PATHS.ACCOUNTS,
    href: accountsFamilyPath(user.family.id),
    icon: AccountsIcon,
    isTitleMono: true,
  };
}
